import DropDown from "../../shared/DropDown";
import bridge from "../../../turbo/bridge";
import {
  faComment,
  faCommentSlash,
  faLock,
  faMailbox,
  faStar as faStarSolid,
  faThumbTack,
  faThumbTackSlash,
  faUser,
  faFlag,
} from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";
import ChangeAuthorForm from "./contextMenuForms/ChangeAuthorForm.tsx";
import { every, forEach, get, isEmpty, map } from "lodash";
import {
  faChartSimple,
  faBoxArchive,
  faBoxOpen,
  faClone,
  faPencil,
  faMailbox as faMailboxRegular,
  faPrint,
  faCheckSquare,
  faSquare,
  faFileCsv,
  faFileExcel,
  faStar as faStarRegular,
  faShareNodes,
  faBullhorn,
  faEye,
  faEyeSlash,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { Activitystream } from "../../../api/activitystreams.ts";
import { Activity, ActivityObject } from "../../../@types/activities.ts";
import EditActivityForm from "./contextMenuForms/EditActivityForm.tsx";
import { hasOwnPage, ownPage } from "../ownPageObjects.ts";
import { useUpdateActivityInCache } from "../../../hooks/activitystreams/useUpdateActivityInCache.ts";
import ReplaceStickiesModal from "./ReplaceStickiesModal.tsx";
import {
  StickySibling,
  useCloseSurvey,
  useDestroyActivity,
  useFetchStickySiblings,
  useIgnoreAuthor,
  useSetSticky,
  useToggleArchived,
  useToggleBookmark,
  useToggleHidden,
  useToggleNewsletterActivity,
  useToggleRecommendation,
  useToggleRepliesClosed,
  useToggleSubscriptionEmail,
} from "./api.ts";
import queryClient from "../../../helpers/queryClient.tsx";
import { waitForActivityImages } from "../printActivity.ts";
import ActivityShareMenuItems, {
  ActivityShareMenu,
} from "../share/ActivityShareMenuItems.tsx";
import { openResults, resultsPath } from "../../questionnaires/resultsLink.ts";

type ActivityContextMenuArgs = {
  activityId: string;
  activityCan: Activity["can"];
  streamUrl?: string;
  streamsFromActivity?: Activitystream[];
  repliesClosed?: Activity["replies_closed"];
  plannedForNewsletterAt?: Activity["planned_for_newsletter_at"];
  share?: ActivityShareMenu;
  objectId?: string;
  sticky?: Activity["sticky"];
  archivedAt?: Activity["archived_at"];
  hidden?: Activity["hidden"];
  iSubscribedEmail?: Activity["i_subscribed_email"];
  actor: Activity["actor"];
  iBookmarked?: ActivityObject["i_bookmarked?"];
  iRecommend?: ActivityObject["i_recommend?"];
  closable?: boolean;
  closed?: boolean;
} & Omit<ActivityObject, "id" | "i_bookmarked?" | "i_recommend?">;

export default function ActivityContextMenu({
  activityId,
  streamsFromActivity,
  activityCan,
  type,
  objectId,
  repliesClosed,
  plannedForNewsletterAt,
  iSubscribedEmail,
  sticky,
  archivedAt,
  hidden,
  streamUrl,
  can,
  closable,
  closed,
  bookmarkable,
  share,
  iBookmarked,
  recommendable,
  iRecommend,
  actor,
}: ActivityContextMenuArgs) {
  const [showChangeAuthorModal, setShowChangeAuthorModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);

  const { updateActivity, removeActivity } = useUpdateActivityInCache();

  const { mutate: toggleRepliesClosed } = useToggleRepliesClosed(
    activityId,
    repliesClosed,
    {
      onSuccess: ({ replies_closed }: { replies_closed: boolean }) =>
        updateActivity(activityId, (activity) => ({
          ...activity,
          replies_closed,
        })),
    },
  );

  const { mutate: toggleNewsletterActivity } = useToggleNewsletterActivity(
    activityId,
    plannedForNewsletterAt,
    {
      onSuccess: () =>
        updateActivity(
          activityId,
          (activity) =>
            ({
              ...activity,
              planned_for_newsletter_at: plannedForNewsletterAt
                ? null
                : new Date().toISOString(),
            }) as Activity,
        ),
    },
  );

  const { mutate: setSticky } = useSetSticky(activityId, {
    onSuccess: (_data, { sticky, replacedIds }) => {
      streamUrl &&
        queryClient.invalidateQueries(`${streamUrl}/sticky/activities`);
      updateActivity(
        activityId,
        (activity) =>
          ({
            ...activity,
            sticky: sticky === false ? null : sticky,
          }) as Activity,
      );
      // the replaced pins are gone server-side; nulling sticky also drops
      // them from the pinned box (see useUpdateActivityInCache)
      forEach(replacedIds, (replacedId) =>
        updateActivity(
          replacedId,
          (activity) => ({ ...activity, sticky: null }) as Activity,
        ),
      );
    },
  });

  // Pinning asks first when other posts of the same context are pinned (#283):
  // the modal offers to replace them, keep them, or not pin at all.
  const [pendingSticky, setPendingSticky] = useState<{
    sticky: "regular" | "confirmable";
    siblings: StickySibling[];
  }>();
  const { mutateAsync: fetchStickySiblings } =
    useFetchStickySiblings(activityId);

  const requestSticky = async (sticky: "regular" | "confirmable") => {
    let siblings: StickySibling[];
    try {
      siblings = await fetchStickySiblings();
    } catch {
      toastr.error(I18n.t("js.generic_error"));
      return;
    }
    if (isEmpty(siblings)) setSticky({ sticky });
    else setPendingSticky({ sticky, siblings });
  };

  const decideSticky = (replace: boolean) => {
    if (!pendingSticky) return;
    setSticky({
      sticky: pendingSticky.sticky,
      replace,
      replacedIds: replace ? map(pendingSticky.siblings, "id") : undefined,
    });
    setPendingSticky(undefined);
  };

  const { mutate: toggleSubscription } = useToggleSubscriptionEmail(
    activityId,
    !!iSubscribedEmail,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          i_subscribed_email: !iSubscribedEmail,
        }));
      },
    },
  );

  const { mutate: closeSurvey } = useCloseSurvey(activityId, type, {
    onSuccess: () => {
      updateActivity(activityId, (activity) => ({
        ...activity,
        object: { ...activity.object, ["closed"]: true },
      }));
    },
  });

  const { mutate: toggleBookmark } = useToggleBookmark(
    activityId,
    !!iBookmarked,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: { ...activity.object, ["i_bookmarked?"]: !iBookmarked },
        }));
      },
    },
  );

  const { mutate: toggleRecommendation } = useToggleRecommendation(
    activityId,
    !!iRecommend,
    {
      onSuccess: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: {
            ...activity.object,
            ["i_recommend?"]: !iRecommend,
            recommendation_count: iRecommend
              ? (activity.object.recommendation_count || 1) - 1
              : (activity.object.recommendation_count || 0) + 1,
          },
        }));
      },
    },
  );

  const { mutate: toggleHidden } = useToggleHidden(activityId, !!hidden, {
    onSuccess: ({ hidden: nowHidden }) => {
      // The activity leaves the list the member is looking at either way:
      // hiding drops it from the stream, showing it again drops it from the
      // hidden-posts tab.
      removeActivity(activityId);
      // removeActivity only touches the list caches. The detail-page cache is
      // keyed by the plain string "/activities/:id", which neither
      // removeActivity nor invalidateQueries(["activities"]) matches — patch it
      // explicitly so the context menu there flips its label without a reload.
      updateActivity(activityId, (activity) => ({
        ...activity,
        hidden: nowHidden,
      }));
      // The *other* lists (the hidden-posts tab resp. the streams) have to pick
      // the activity up, but they are not on screen: mark them stale so they
      // refetch on their next mount instead of refetching the — already
      // corrected — active list page by page.
      queryClient.invalidateQueries(["activities"], { refetchActive: false });
      toastr.success(
        I18n.t(
          nowHidden
            ? "js.activitystreams.context_menu.hide.success"
            : "js.activitystreams.context_menu.unhide.success",
        ),
      );
    },
  });

  const { mutate: ignoreAuthor } = useIgnoreAuthor(actor.id, {
    onSuccess: () => {
      removeActivity(activityId);

      toastr.success(
        I18n.t("js.activitystreams.context_menu.ignore_author.success", {
          actor_name: actor.displayName,
        }),
      );
    },
  });

  const { mutate: destroyActivity } = useDestroyActivity(activityId, {
    onSuccess: () => {
      removeActivity(activityId);
    },
  });

  const { mutate: toggleArchived } = useToggleArchived(
    activityId,
    !!archivedAt,
    {
      onSuccess: (data) => {
        // The activity leaves the list the member is looking at either way:
        // archiving hides it from the stream, unarchiving removes it from the
        // "archived" filter. The detail-page cache keeps the updated fields.
        removeActivity(activityId);
        updateActivity(activityId, (activity) => ({
          ...activity,
          archived_at: (data as Activity).archived_at,
          archived_by_name: (data as Activity).archived_by_name,
        }));
        toastr.success(
          I18n.t(
            archivedAt
              ? "js.activity.successfully_unarchived"
              : "js.activity.successfully_archived",
          ),
        );
      },
    },
  );

  function checkNewsletterConditions(onProceed: () => void) {
    if (plannedForNewsletterAt) {
      onProceed();
      return;
    }

    if (every(streamsFromActivity, (s) => s.hidden)) {
      bridge.confirm(
        I18n.t(
          "js.activitystreams.newsletter.add_to_newsletter_hidden_confirmation",
        ),
        onProceed,
      );
      return;
    }

    if (every(streamsFromActivity, (s) => s.closed)) {
      bridge.confirm(
        I18n.t(
          "js.activitystreams.newsletter.add_to_newsletter_closed_confirmation",
        ),
        onProceed,
      );
      return;
    }

    onProceed();
  }

  function onToggleNewsletterActivity() {
    checkNewsletterConditions(toggleNewsletterActivity);
  }

  return (
    <div className="activity-context-menu context-menu-wrap">
      <DropDown.Wrapper>
        <DropDown.Toggle
          className="context-menu"
          buttonType="normal"
          iconSize={window.isApp ? "xl" : undefined}
          small
        />
        <DropDown>
          <DropDown.Item
            icon={faClone}
            text={I18n.t("js.activitystreams.permalink_copy_link")}
            onSelect={() => {
              navigator.clipboard
                .writeText(
                  `https://${Preload.current_network.host}/activities/${activityId}`,
                )
                .then(() =>
                  toastr.success(
                    I18n.t("js.activitystreams.directlink_copied"),
                  ),
                );
            }}
            dividerBottom
          />
          {activityCan.update_object && (
            <DropDown.Item
              text={I18n.t(
                ownPage(type)?.editText || "js.activitystreams.edit",
              )}
              icon={faPencil}
              onSelect={
                hasOwnPage(type)
                  ? undefined
                  : () =>
                      window.isApp
                        ? bridge.navigateInApp(`/activities/${activityId}/edit`)
                        : setShowEditActivityModal(true)
              }
              url={objectId ? ownPage(type)?.editUrl(objectId) : undefined}
            />
          )}
          {activityCan.update_activitystreams && hasOwnPage(type) && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.edit")}
              icon={faPencil}
              onSelect={() =>
                !window.isApp
                  ? setShowEditActivityModal(true)
                  : bridge.navigateInApp(`/activities/${activityId}/edit`)
              }
            />
          )}
          {activityCan.update_actor && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.change_author.title")}
              icon={faUser}
              onSelect={() => setShowChangeAuthorModal(true)}
            />
          )}
          {(activityCan.update_actor ||
            activityCan.update_activitystreams ||
            activityCan.update_object) && <li className="divider" />}
          {activityCan.close_replies && (
            <DropDown.Item
              icon={repliesClosed ? faComment : faCommentSlash}
              text={I18n.t(
                repliesClosed
                  ? "js.activitystreams.comments.enable"
                  : "js.activitystreams.comments.disable",
              )}
              onSelect={() => toggleRepliesClosed()}
              dividerTop={
                activityCan.update_actor ||
                activityCan.update_activitystreams ||
                activityCan.update_object
              }
              dividerBottom
            />
          )}
          {activityCan.add_to_newsletter &&
            (!streamsFromActivity?.length ||
              !every(streamsFromActivity, (s) => s.sensitive)) && (
              <DropDown.Item
                icon={plannedForNewsletterAt ? faMailbox : faMailboxRegular}
                text={I18n.t(
                  plannedForNewsletterAt
                    ? "js.activitystreams.newsletter.remove_from_newsletter"
                    : "js.activitystreams.newsletter.add_to_newsletter",
                )}
                onSelect={() => onToggleNewsletterActivity()}
              />
            )}
          <DropDown.Item
            icon={faPrint}
            text={I18n.t("activitystreams.print")}
            {...(window.location.pathname.endsWith(`/activities/${activityId}`)
              ? // On the activity detail page the page IS this single activity,
                // so print directly (deferred past the dropdown-close re-render
                // so the menu isn't captured in the print output) once the
                // images, incl. the lazy print-only tiles, are loaded.
                {
                  onSelect: () =>
                    requestAnimationFrame(() =>
                      requestAnimationFrame(() =>
                        waitForActivityImages().then(() => window.print()),
                      ),
                    ),
                }
              : // Elsewhere (e.g. the stream/Verlauf) printing the current page
                // would print the whole list — open the activity's detail page
                // with #print first; ActivityPage auto-prints on load.
                window.isApp
                ? {
                    onSelect: () =>
                      bridge.navigateInApp(`/activities/${activityId}#print`),
                  }
                : { url: `/activities/${activityId}#print` })}
          />
          {activityCan.update_sticky &&
            (sticky ? (
              <DropDown.Item
                icon={faThumbTackSlash}
                text={I18n.t("activitystreams.sticky.reset")}
                onSelect={() => setSticky({ sticky: false })}
              />
            ) : (
              <>
                {activityCan.set_regular_sticky && (
                  <DropDown.Item
                    icon={faThumbTack}
                    text={`${I18n.t("activitystreams.sticky.set")}: ${I18n.t(
                      "activitystreams.sticky.regular",
                    )}`}
                    onSelect={() => requestSticky("regular")}
                  />
                )}
                <DropDown.Item
                  icon={faThumbTack}
                  text={`${I18n.t("activitystreams.sticky.set")}: ${I18n.t(
                    "activitystreams.sticky.confirmable",
                  )}`}
                  onSelect={() => requestSticky("confirmable")}
                />
              </>
            ))}
          <DropDown.Item
            icon={iSubscribedEmail ? faCheckSquare : faSquare}
            text={I18n.t("activitystreams.subscribed_email")}
            onSelect={() => toggleSubscription()}
            dividerTop
          />
          {type === "survey" && activityCan.export_survey_results && (
            <>
              {map(["csv", "xlsx"], (exportType) => (
                <DropDown.Item
                  key={exportType}
                  text={I18n.t(`activitystreams.survey.export.${exportType}`)}
                  icon={exportType === "csv" ? faFileCsv : faFileExcel}
                  url={`/activities/${activityId}/survey_results.${exportType}`}
                  dividerTop={exportType === "csv"}
                />
              ))}
            </>
          )}
          {type === "questionnaire" && objectId && get(can, "results") && (
            <DropDown.Item
              text={I18n.t("js.questionnaires.results.menu")}
              icon={faChartSimple}
              url={resultsPath(objectId)}
              onSelect={window.isApp ? undefined : () => openResults(objectId)}
              dividerTop
            />
          )}
          {can?.close && closable && !closed && (
            <DropDown.Item
              icon={faLock}
              text={I18n.t(`js.activitystreams.close_${type}`)}
              onSelect={() => closeSurvey()}
              dividerTop
            />
          )}
          {can?.bookmark && bookmarkable && (
            <DropDown.Item
              text={I18n.t(
                `js.activitystreams.${iBookmarked ? "unbookmark" : "bookmark"}`,
              )}
              icon={iBookmarked ? faStarSolid : faStarRegular}
              onSelect={() => toggleBookmark()}
              dividerTop
            />
          )}
          {share?.options.hasAny && (
            <DropDown.SubMenu
              text={I18n.t("activitystreams.share")}
              icon={faShareNodes}
            >
              <ActivityShareMenuItems share={share} />
            </DropDown.SubMenu>
          )}
          {can?.recommend && recommendable && (
            <DropDown.Item
              icon={faFlag}
              text={I18n.t(
                `js.activitystreams.${iRecommend ? "unrecommend" : "recommend"}`,
              )}
              onSelect={() => toggleRecommendation()}
            />
          )}
          {activityCan.report && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.report")}
              icon={faBullhorn}
              url={`/activities/${activityId}/report/new`}
              dividerTop
            />
          )}
          {Preload.current_member?.id &&
            actor.id !== Preload.current_member.id &&
            actor.type !== "blog" && (
              <DropDown.Item
                text={I18n.t("js.activitystreams.ignore", {
                  name: actor.displayName,
                })}
                icon={faEyeSlash}
                onSelect={() =>
                  bridge.confirm(
                    I18n.t(
                      "js.activitystreams.context_menu.ignore_author.confirmation",
                      { actor_name: actor.displayName },
                    ),
                    () => ignoreAuthor(),
                  )
                }
                dividerTop
              />
            )}
          {activityCan.hide && (
            <DropDown.Item
              text={I18n.t(
                hidden
                  ? "js.activitystreams.context_menu.unhide.label"
                  : "js.activitystreams.context_menu.hide.label",
              )}
              icon={hidden ? faEye : faEyeSlash}
              onSelect={() =>
                hidden
                  ? toggleHidden()
                  : bridge.confirm(
                      I18n.t(
                        "js.activitystreams.context_menu.hide.confirmation",
                      ),
                      () => toggleHidden(),
                    )
              }
              dividerTop
            />
          )}
          {activityCan.archive && (
            <DropDown.Item
              text={I18n.t(
                archivedAt
                  ? "js.activitystreams.unarchive"
                  : "js.activitystreams.archive",
              )}
              icon={archivedAt ? faBoxOpen : faBoxArchive}
              onSelect={() =>
                archivedAt
                  ? toggleArchived()
                  : bridge.confirm(
                      I18n.t("js.activity.archive_confirmation"),
                      () => toggleArchived(),
                    )
              }
              dividerTop={!activityCan.hide}
            />
          )}
          {activityCan.destroy && (
            <DropDown.Item
              text={I18n.t("js.activitystreams.delete")}
              icon={faTrash}
              onSelect={() =>
                bridge.confirm(I18n.t("js.activity.delete_confirmation"), () =>
                  destroyActivity(),
                )
              }
              dividerTop={!activityCan.archive && !activityCan.hide}
            />
          )}
        </DropDown>
      </DropDown.Wrapper>
      {pendingSticky && (
        <ReplaceStickiesModal
          siblings={pendingSticky.siblings}
          onDecision={decideSticky}
          onClose={() => setPendingSticky(undefined)}
        />
      )}
      {showEditActivityModal && (
        <EditActivityForm
          activityId={activityId}
          close={() => setShowEditActivityModal(false)}
        />
      )}
      {showChangeAuthorModal && (
        <ChangeAuthorForm
          activityId={activityId}
          close={() => setShowChangeAuthorModal(false)}
        />
      )}
    </div>
  );
}
